import { useEffect, useState } from "react"
import { faCartShopping } from "@fortawesome/pro-solid-svg-icons"
import { faCircleInfo } from "@fortawesome/pro-thin-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, IconButton, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useMoney } from "../../../../../modules/currency/useMoney"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useAppSelector } from "../../../../../modules/store/storeHooks"
import {
    selectEffectiveCompanyId,
    selectEffectiveUserId,
    selectEffectiveUserType,
} from "../../../../../modules/context/contextSelectors"
import { UserType } from "../../../../../modules/login/loginTypes"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { WebshopType } from "../../../../../modules/payments/orderTypes"
import { StandardButton } from "../../../buttons/StandardButton"
import { IconBadge } from "../../../badges/IconBadge"
import { CreateOrderDialogWrap } from "../CreateOrderDialogWrap"
import { CreateOrderDialogTitle } from "../CreateOrderDialogTitle"
import type { SelectedMainUserState } from "../../createOrderTypes"

import { TakeawayCartProvider, useTakeawayCart } from "./TakeawayCartContext"
import { TakeawayFormContainer } from "./TakeawayFormContainer"
import type { TakeawayKitchensResponse } from "./takeawayTypes"
import { fetchUserShops, TakeawayQuery, UserShopType } from "./takeawayApi"
import { calcItemsTotal } from "./components/takeawayCheckoutLib"
import { TakeawayKitchenSelectorDialog } from "./components/TakeawayKitchenSelectorDialog"
import { TakeawayStoreDetailsDialog } from "./components/TakeawayStoreDetailsDialog"

function selectKitchens(data: TakeawayKitchensResponse) {
    return {
        ...data,
        kitchens: data.kitchens.filter((kitchen) => kitchen.webshop?.orderType === WebshopType.TAKE_AWAY),
    }
}

const useStyles = makeStyles()((theme) => ({
    layout: {
        display: "flex",
        flexDirection: "column",
        height: "100svh",
        overflow: "hidden",
    },
    titleRow: {
        display: "flex",
        alignItems: "center",
        gap: ".375rem",
        flex: 1,
        minWidth: 0,
    },
    title: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: "1rem",
        fontWeight: 400,
    },
    infoButton: {
        flexShrink: 0,
        color: theme.palette.text.primary,
        padding: ".25rem",
    },
    showOrderButton: {
        flexShrink: 0,
        whiteSpace: "nowrap",
    },
    iconSize: {
        fontSize: "1.25rem !important",
    },
}))

type Props = Readonly<{
    open: boolean
    onClose: () => void
    onBehalfUser?: SelectedMainUserState
}>

type TakeawayOrderDialogContentProps = Readonly<{
    onClose: () => void
}>

type TakeawayOrderDialogFlowProps = Readonly<{
    open: boolean
    onClose: () => void
}>

function TakeawayOrderDialogContent({ onClose }: TakeawayOrderDialogContentProps) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})
    const cart = useTakeawayCart()
    const [showStoreDetails, setShowStoreDetails] = useState(false)

    const allItems = Object.values(cart?.cartByDate ?? {}).flat()
    const cartTotal = calcItemsTotal(allItems)

    const selectedKitchen = cart?.selectedKitchen
    const kitchenName = selectedKitchen?.name

    return (
        <div className={classes.layout}>
            <CreateOrderDialogTitle handleClose={onClose}>
                <>
                    <Box className={classes.titleRow}>
                        {kitchenName ? (
                            <LocalizedStrict id="create-order-take-away-title-with-kitchen" vars={{ kitchenName }}>
                                <Typography className={classes.title}>Takeaway - {kitchenName}</Typography>
                            </LocalizedStrict>
                        ) : (
                            <LocalizedStrict id="create-order-take-away-title">
                                <Typography className={classes.title}>Takeaway</Typography>
                            </LocalizedStrict>
                        )}
                        {selectedKitchen && (
                            <IconButton
                                className={classes.infoButton}
                                size="small"
                                onClick={() => setShowStoreDetails(true)}
                            >
                                <FontAwesomeIcon icon={faCircleInfo} />
                            </IconButton>
                        )}
                    </Box>

                    {cartTotal ? (
                        <StandardButton
                            color="black"
                            fullWidth={false}
                            size="large"
                            startIcon={<FontAwesomeIcon icon={faCartShopping} className={classes.iconSize} />}
                            onClick={cart?.onShowOrder}
                            className={classes.showOrderButton}
                        >
                            <LocalizedStrict
                                id="takeaway-show-order-button"
                                vars={{ price: moneyFactory.format(cartTotal) }}
                            >
                                <span>Show order: {moneyFactory.format(cartTotal)}</span>
                            </LocalizedStrict>
                        </StandardButton>
                    ) : (
                        <IconBadge variant="font-awesome" color="blue" fontAwesomeIcon={faCartShopping} />
                    )}
                </>
            </CreateOrderDialogTitle>

            <TakeawayFormContainer onClose={onClose} />

            {showStoreDetails && selectedKitchen && (
                <TakeawayStoreDetailsDialog kitchen={selectedKitchen} onClose={() => setShowStoreDetails(false)} />
            )}
        </div>
    )
}

function TakeawayOrderDialogFlow({ open, onClose }: TakeawayOrderDialogFlowProps) {
    const cart = useTakeawayCart()
    const merchantId = useAppSelector(selectEffectiveCompanyId)
    const effectiveUserId = useAppSelector(selectEffectiveUserId)
    const isUserPortal = useAppSelector(selectEffectiveUserType) === UserType.USER
    const onBehalfUserId = cart?.onBehalfUser?.userId

    const shopsUserId = isUserPortal ? effectiveUserId : onBehalfUserId

    const { data: kitchensData, isInitialLoading: isLoadingKitchens } = useApiQuery(fetchUserShops, {
        queryName: TakeawayQuery.TAKEAWAY_USER_SHOPS,
        dependencies: {
            userId: shopsUserId ?? 0,
            merchantId: onBehalfUserId ? merchantId : undefined,
            type: UserShopType.TAKEAWAY,
        },
        enabled: Boolean(shopsUserId) && (isUserPortal || Boolean(merchantId)),
        select: selectKitchens,
    })

    const kitchens = kitchensData?.kitchens ?? []

    useEffect(() => {
        if (!kitchensData || !cart || cart.selectedKitchen) return
        if (kitchensData.kitchens.length === 1) {
            cart.onKitchenSelect(kitchensData.kitchens[0])
        }
    }, [kitchensData, cart])

    const showKitchenSelector = !isLoadingKitchens && !cart?.selectedKitchen && kitchens.length !== 1

    return (
        <CreateOrderDialogWrap onBehalfOf={false} open={open} onClose={onClose}>
            <TakeawayOrderDialogContent onClose={onClose} />
            {showKitchenSelector && (
                <TakeawayKitchenSelectorDialog
                    kitchens={kitchens}
                    onSelect={(kitchen) => cart?.onKitchenSelect(kitchen)}
                    onClose={onClose}
                />
            )}
        </CreateOrderDialogWrap>
    )
}

export function TakeawayOrderDialog({ open, onClose, onBehalfUser }: Props) {
    return (
        <TakeawayCartProvider onBehalfUser={onBehalfUser}>
            <TakeawayOrderDialogFlow open={open} onClose={onClose} />
        </TakeawayCartProvider>
    )
}
